import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Save, Users, Briefcase, Loader2, AlertCircle, DollarSign, Coins } from 'lucide-react';

const SCOPE_LABELS = {
  player_referral: { title: 'Player Referral: 33% First Deposit Cash', icon: Users, color: '#22c55e' },
  staff_referral:  { title: 'Agent Referral: 33% First Deposit Cash',  icon: Briefcase, color: '#D4AF37' },
};

const WINDOW_OPTIONS = [
  { value: 'first_n_deposits', label: 'First deposit only' },
  { value: 'lifetime', label: 'Lifetime — every deposit, forever' },
  { value: 'first_x_total', label: 'Until referee\'s cumulative deposits hit $X' },
  { value: 'first_n_days', label: 'Within N days from referee signup' },
];

function RuleCard({ rule, onSave, saving }) {
  const meta = SCOPE_LABELS[rule.scope] || { title: rule.scope, icon: Users, color: '#888' };
  const [draft, setDraft] = useState(rule);
  useEffect(() => setDraft(rule), [rule]);

  const Icon = meta.icon;

  // Live preview — what would a $50 deposit actually pay out?
  const sampleDeposit = 50;
  const pct = parseFloat(draft.commission_pct) || 0;
  const cap = parseFloat(draft.max_payout_per_deposit_usd) || 0;
  const samplePayout = Math.min(sampleDeposit * pct / 100, cap > 0 ? cap : Infinity);
  const currencyLabel = draft.reward_currency === 'cash' ? 'USD wallet' : 'FP credits';

  const handleSave = () => {
    onSave(draft.scope, {
      enabled: !!draft.enabled,
      commission_pct: parseFloat(draft.commission_pct) || 0,
      reward_currency: draft.reward_currency || 'fp',
      window_type: draft.window_type || 'lifetime',
      window_first_n_deposits: parseInt(draft.window_first_n_deposits) || 0,
      window_first_x_total_usd: parseFloat(draft.window_first_x_total_usd) || 0,
      window_first_n_days: parseInt(draft.window_first_n_days) || 0,
      min_deposit_usd: parseFloat(draft.min_deposit_usd) || 0,
      max_payout_per_deposit_usd: parseFloat(draft.max_payout_per_deposit_usd) || 0,
      per_referee_lifetime_cap_usd: parseFloat(draft.per_referee_lifetime_cap_usd) || 0,
    });
  };

  return (
    <div
      className="rounded-2xl p-5 border space-y-4"
      style={{
        background: `linear-gradient(135deg, ${meta.color}10, rgba(255,255,255,0.02))`,
        borderColor: `${meta.color}40`,
      }}
      data-testid={`lre-rule-card-${rule.scope}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${meta.color}25`, border: `1px solid ${meta.color}55` }}
          >
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-white">{meta.title}</h3>
            <p className="text-[11px] text-gray-400 leading-snug">
              Referrer earns real cash in main balance. Referred player still gets the 100% first-deposit bonus in FP.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={!!draft.enabled}
            onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))}
            className="w-4 h-4 accent-emerald-500"
            data-testid={`lre-enabled-${rule.scope}`}
          />
          <span className={draft.enabled ? 'text-emerald-400 font-bold' : 'text-gray-400'}>
            {draft.enabled ? 'ENABLED' : 'DISABLED'}
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">First Deposit Commission %</label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number" min="0" max="50" step="0.5"
              value={draft.commission_pct}
              onChange={e => setDraft(d => ({ ...d, commission_pct: e.target.value }))}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm tabular-nums"
              data-testid={`lre-commission-pct-${rule.scope}`}
            />
            <span className="text-sm text-gray-400">%</span>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Reward Currency</label>
          <select
            value={draft.reward_currency}
            onChange={e => setDraft(d => ({ ...d, reward_currency: e.target.value }))}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1"
            data-testid={`lre-currency-${rule.scope}`}
          >
            <option value="fp">FP Credits (in-game only)</option>
            <option value="cash">USD Wallet (real cash, withdrawable)</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Earning Window</label>
          <select
            value={draft.window_type}
            onChange={e => setDraft(d => ({ ...d, window_type: e.target.value }))}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1"
            data-testid={`lre-window-${rule.scope}`}
          >
            {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {draft.window_type === 'first_n_deposits' && (
          <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Number of deposits</label>
            <input
              type="number" min="1" max="999"
              value={draft.window_first_n_deposits}
              onChange={e => setDraft(d => ({ ...d, window_first_n_deposits: e.target.value }))}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
              data-testid={`lre-window-n-deposits-${rule.scope}`}
            />
          </div>
        )}
        {draft.window_type === 'first_x_total' && (
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">$X cumulative deposits</label>
            <input
              type="number" min="1" step="10"
              value={draft.window_first_x_total_usd}
              onChange={e => setDraft(d => ({ ...d, window_first_x_total_usd: e.target.value }))}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
              data-testid={`lre-window-x-total-${rule.scope}`}
            />
          </div>
        )}
        {draft.window_type === 'first_n_days' && (
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">N days from signup</label>
            <input
              type="number" min="1" max="3650"
              value={draft.window_first_n_days}
              onChange={e => setDraft(d => ({ ...d, window_first_n_days: e.target.value }))}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
              data-testid={`lre-window-n-days-${rule.scope}`}
            />
          </div>
        )}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Min Deposit ($)</label>
          <input
            type="number" min="0" step="1"
            value={draft.min_deposit_usd}
            onChange={e => setDraft(d => ({ ...d, min_deposit_usd: e.target.value }))}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
            data-testid={`lre-min-deposit-${rule.scope}`}
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Max Payout / Deposit ($)</label>
          <input
            type="number" min="0" step="5"
            value={draft.max_payout_per_deposit_usd}
            onChange={e => setDraft(d => ({ ...d, max_payout_per_deposit_usd: e.target.value }))}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
            data-testid={`lre-max-per-deposit-${rule.scope}`}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Per-Referee Lifetime Cap ($) — 0 means no cap</label>
          <input
            type="number" min="0" step="50"
            value={draft.per_referee_lifetime_cap_usd}
            onChange={e => setDraft(d => ({ ...d, per_referee_lifetime_cap_usd: e.target.value }))}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md p-2 text-sm mt-1 tabular-nums"
            data-testid={`lre-per-referee-cap-${rule.scope}`}
          />
        </div>
      </div>

      {/* Live preview */}
      <div
        className="rounded-lg p-3 text-xs flex items-start gap-2"
        style={{ background: `${meta.color}10`, border: `1px dashed ${meta.color}55` }}
      >
        <Coins className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
        <div>
          <p className="text-gray-300">
            <span className="font-bold">Preview:</span> A referral makes a first deposit of{' '}
            <span className="tabular-nums font-bold text-white">${sampleDeposit.toFixed(2)}</span>{' '}
            → referrer gets real cash{' '}
            <span className="tabular-nums font-bold" style={{ color: meta.color }}>
              ${isFinite(samplePayout) ? samplePayout.toFixed(2) : '0.00'} {currencyLabel}
            </span>
          </p>
          {parseFloat(draft.min_deposit_usd) > 0 && (
            <p className="text-gray-500 text-[10px] mt-1">
              Deposits below ${draft.min_deposit_usd} pay $0.
            </p>
          )}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg font-bold text-sm transition-all anim-press disabled:opacity-50"
        style={{ background: meta.color, color: '#0a0a0a' }}
        data-testid={`lre-save-${rule.scope}`}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save Rule'}
      </button>
    </div>
  );
}

export default function LREEarningRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchAll = async () => {
    try {
      const [rRes, sRes] = await Promise.all([
        api.get('/api/admin/referrals/lre/rules'),
        api.get('/api/admin/referrals/lre/summary?days=30'),
      ]);
      setRules(rRes.data.rules || []);
      setSummary(sRes.data || null);
    } catch (err) {
      toast.error('Failed to load LRE rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = async (scope, payload) => {
    setSaving(scope);
    try {
      await api.put(`/api/admin/referrals/lre/rules/${scope}`, payload);
      toast.success('Rule saved');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  }

  return (
    <div className="space-y-5 anim-fadeInUp" data-testid="lre-rules-page">
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-[#D4AF37]" /> Referral Earnings Rules
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Set commission rules for player→player and staff→player referrals.
          Triggered by every successful deposit (gateway + manual).
          Idempotent — duplicate webhooks never double-pay.
        </p>
      </div>

      {summary && summary.by_scope && summary.by_scope.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lre-summary-section">
          {summary.by_scope.map(s => {
            const meta = SCOPE_LABELS[s.scope] || {};
            return (
              <div key={s.scope}
                   className="rounded-xl p-4 border border-white/5 flex flex-col gap-1"
                   style={{ background: 'rgba(255,255,255,0.02)' }}>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{meta.title || s.scope}</p>
                <p className="text-sm text-gray-300">
                  Last {summary.window_days}d:{' '}
                  <span className="font-bold tabular-nums" style={{ color: '#22c55e' }}>${s.cash_total.toFixed(2)}</span>
                  <span className="text-gray-500"> + </span>
                  <span className="font-bold tabular-nums" style={{ color: '#A5B4FC' }}>{s.fp_total.toFixed(2)} FP</span>
                </p>
                <p className="text-[10px] text-gray-500">
                  {s.count} payouts · {s.unique_referrers} earners · {s.unique_referees} referees
                </p>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rules.map(r => (
          <RuleCard key={r.scope} rule={r} onSave={handleSave} saving={saving === r.scope} />
        ))}
      </div>

      <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.20)' }}>
        <AlertCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-300 leading-snug">
          <strong className="text-emerald-400">Rule:</strong> Referrers earn 33% real cash from the referred player's first deposit.
          The referred player's first-deposit bonus stays 100% FP.
        </p>
      </div>
    </div>
  );
}
